import React, { useCallback, useContext, useEffect, useState } from 'react';
import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import {
  Button,
  DatePicker,
  Drawer,
  Form,
  Input,
  Modal,
  Popconfirm,
  Select,
  Space,
  Spin,
} from 'antd';
import { ColumnProps } from 'antd/lib/table';
import dayjs, { Dayjs } from 'dayjs';

import {
  PHASE_STATUSES,
  Program,
  ProgramPhase,
} from '@totem/components/portfolio/buildings/details/optimize/types';
import IBTable from '@totem/components/ui_components/antDesignOverrides/IBTable';
import ModalFooter from '@totem/components/ui_components/dialogs/ModalFooter';
import ModalTitle from '@totem/components/ui_components/dialogs/ModalTitle';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { BUILDINGS_ENDPOINT } from '@totem/utilities/endpoints';
import { CheckResponseShowError } from '@totem/utilities/responseUtilities';

const styles = {
  row: { display: 'flex', gap: '4%' },
  quarterWidth: { paddingBottom: '0', marginBottom: '1rem', width: '25%' },
  threeQuarterWidth: {
    paddingBottom: '0',
    marginBottom: '1rem',
    width: '71%',
  },
  halfWidth: { paddingBottom: '0', marginBottom: '1rem', width: '48%' },
  formItem: { paddingBottom: '0', marginBottom: '1rem', width: '100%' },
};

// ─── Phase add/edit modal ────────────────────────────────────────────────────

type PhaseModalProps = {
  open: boolean;
  phase: ProgramPhase | null;
  nextPhaseNumber: number;
  onClose: () => void;
  onSave: (phase: ProgramPhase) => void;
};

const PhaseModal = ({
  open,
  phase,
  nextPhaseNumber,
  onClose,
  onSave,
}: PhaseModalProps) => {
  const [phaseNum, setPhaseNum] = useState(1);
  const [name, setName] = useState('');
  const [status, setStatus] = useState('pending');
  const [startedAt, setStartedAt] = useState<Dayjs | null>(null);
  const [completedAt, setCompletedAt] = useState<Dayjs | null>(null);

  useEffect(() => {
    if (open) {
      if (phase) {
        setPhaseNum(phase.phase);

        setName(phase.name);

        setStatus(phase.status);

        setStartedAt(phase.startedAt ? dayjs(phase.startedAt) : null);

        setCompletedAt(phase.completedAt ? dayjs(phase.completedAt) : null);
      } else {
        setPhaseNum(nextPhaseNumber);

        setName('');

        setStatus('pending');

        setStartedAt(null);

        setCompletedAt(null);
      }
    }
  }, [open, phase, nextPhaseNumber]);

  const handleSave = () => {
    if (!name.trim()) {
      return;
    }
    onSave({
      phase: phaseNum,
      name,
      status,
      startedAt: startedAt ? startedAt.toISOString() : undefined,
      completedAt: completedAt ? completedAt.toISOString() : undefined,
    });
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      width={480}
      title={<ModalTitle>{phase ? 'Edit Phase' : 'Add Phase'}</ModalTitle>}
      footer={
        <ModalFooter>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="primary" disabled={!name.trim()} onClick={handleSave}>
            {phase ? 'Save' : 'Add'}
          </Button>
        </ModalFooter>
      }
    >
      <Form layout="vertical" style={{ marginTop: 16 }}>
        <div style={styles.row}>
          <Form.Item label="Phase #" style={styles.quarterWidth}>
            <Input
              type="number"
              min={1}
              value={phaseNum}
              onChange={(evt) =>
                setPhaseNum(parseInt(evt.target.value, 10) || 1)
              }
            />
          </Form.Item>
          <Form.Item
            label="Phase Name"
            required
            style={styles.threeQuarterWidth}
          >
            <Input
              value={name}
              onChange={(evt) => setName(evt.target.value)}
              placeholder="e.g. Discovery"
            />
          </Form.Item>
        </div>

        <Form.Item label="Status" style={styles.formItem}>
          <Select
            value={status}
            onChange={setStatus}
            options={PHASE_STATUSES}
          />
        </Form.Item>

        <div style={styles.row}>
          <Form.Item label="Started" style={styles.halfWidth}>
            <DatePicker
              style={{ width: '100%' }}
              value={startedAt}
              onChange={setStartedAt}
            />
          </Form.Item>
          <Form.Item label="Completed" style={styles.halfWidth}>
            <DatePicker
              style={{ width: '100%' }}
              value={completedAt}
              onChange={setCompletedAt}
            />
          </Form.Item>
        </div>
      </Form>
    </Modal>
  );
};

// ─── Main drawer ─────────────────────────────────────────────────────────────

type Props = {
  open: boolean;
  buildingId: string;
  program: Program;
  onClose: () => void;
};

const ProgramPhasesDrawer = ({ open, buildingId, program, onClose }: Props) => {
  const { getApiToken } = useContext(UserProfileContext);
  const [phases, setPhases] = useState<ProgramPhase[]>([]);
  const [saving, setSaving] = useState(false);
  const [phaseModalOpen, setPhaseModalOpen] = useState(false);
  const [editPhase, setEditPhase] = useState<ProgramPhase | null>(null);

  useEffect(() => {
    if (open && program) {
      setPhases(program.phases ?? []);
    }
  }, [open, program]);

  const savePhases = useCallback(
    (updated: ProgramPhase[]) => {
      setSaving(true);

      fetch(
        `${BUILDINGS_ENDPOINT}/${buildingId}/optimize/programs/${program.id}/phases`,
        {
          method: 'PUT',
          headers: new Headers({
            Authorization: `Bearer ${getApiToken()}`,
            'Content-Type': 'application/json',
          }),
          body: JSON.stringify(updated),
        },
      )
        .then((res) => {
          CheckResponseShowError(res);

          setPhases(updated);
        })
        .finally(() => setSaving(false));
    },
    [buildingId, program, getApiToken],
  );

  const handlePhaseModalSave = (saved: ProgramPhase) => {
    const updated = editPhase
      ? phases.map((item) => (item.phase === editPhase.phase ? saved : item))
      : [...phases, saved];
    setPhaseModalOpen(false);

    setEditPhase(null);

    savePhases(updated);
  };

  const handleDelete = (phaseNum: number) => {
    savePhases(phases.filter((item) => item.phase !== phaseNum));
  };

  const phaseStatusLabel = (val: string) =>
    PHASE_STATUSES.find((status) => status.value === val)?.label ?? val;

  const columns: ColumnProps<ProgramPhase>[] = [
    {
      title: '#',
      dataIndex: 'phase',
      key: 'phase',
      width: 50,
      sorter: (compA, compB) => compA.phase - compB.phase,
      defaultSortOrder: 'ascend',
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (_, record) => phaseStatusLabel(record.status),
    },
    {
      title: 'Started',
      dataIndex: 'startedAt',
      key: 'startedAt',
      render: (_, record) =>
        record.startedAt ? dayjs(record.startedAt).format('MMM D, YYYY') : '—',
    },
    {
      title: 'Completed',
      dataIndex: 'completedAt',
      key: 'completedAt',
      render: (_, record) =>
        record.completedAt
          ? dayjs(record.completedAt).format('MMM D, YYYY')
          : '—',
    },
    {
      title: '',
      key: 'actions',
      align: 'right' as const,
      render: (_, record) => (
        <Space>
          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={() => {
              setEditPhase(record);

              setPhaseModalOpen(true);
            }}
          />
          <Popconfirm
            title="Remove this phase?"
            description="This cannot be undone."
            onConfirm={() => handleDelete(record.phase)}
            okText="Remove"
            okType="danger"
          >
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const nextPhaseNumber =
    phases.length > 0 ? Math.max(...phases.map((item) => item.phase)) + 1 : 1;

  return (
    <>
      <Drawer
        open={open}
        onClose={onClose}
        title={`Phases — ${program?.name ?? ''}`}
        width={680}
        extra={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              setEditPhase(null);

              setPhaseModalOpen(true);
            }}
          >
            Add Phase
          </Button>
        }
      >
        <Spin spinning={saving}>
          <IBTable
            columns={columns}
            dataSource={phases}
            rowKey={(record) => String(record.phase)}
            pagination={false}
            size="small"
          />
        </Spin>
      </Drawer>

      <PhaseModal
        open={phaseModalOpen}
        phase={editPhase}
        nextPhaseNumber={nextPhaseNumber}
        onClose={() => {
          setPhaseModalOpen(false);

          setEditPhase(null);
        }}
        onSave={handlePhaseModalSave}
      />
    </>
  );
};

export default ProgramPhasesDrawer;
